// Help.js
import React, { useState } from 'react';
import { 
  Search, ChevronDown, ChevronUp, Book, MessageSquare, 
  Phone, Mail,User, CreditCard, Ambulance, Pill, 
  Stethoscope, Shield, Settings, HelpCircle, 
} from 'lucide-react';

const Help = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const categories = [
    { id: 'all', name: 'All Topics', icon: Book },
    { id: 'getting-started', name: 'Getting Started', icon: User },
    { id: 'consultations', name: 'Consultations', icon: Stethoscope },
    { id: 'emergency', name: 'Emergency Services', icon: Ambulance },
    { id: 'pharmacy', name: 'Pharmacy & Medicine', icon: Pill },
    { id: 'payments', name: 'Payments & Billing', icon: CreditCard },
    { id: 'technical', name: 'Technical Support', icon: Settings },
    { id: 'safety', name: 'Safety & Privacy', icon: Shield }
  ];

  const faqs = [
    {
      category: 'getting-started',
      question: 'How do I register on MediGo?',
      answer: 'To register on MediGo, click the "Register" button on the homepage. Choose whether you are registering as a patient, doctor, hospital, or pharmacy. Fill in your details, verify your email/phone number, and complete your profile. For healthcare providers, additional verification documents will be required.'
    },
    {
      category: 'getting-started',
      question: 'Is MediGo available in my area?',
      answer: 'MediGo is currently available across major cities in Kenya including Nairobi, Mombasa, Kisumu, Nakuru, and Eldoret. We are continuously expanding our network. Enter your location in the app to see available services in your area.'
    },
    {
      category: 'consultations',
      question: 'How do online consultations work?',
      answer: 'After booking an online consultation, you will receive a confirmation with the appointment time. At the scheduled time, you can join the consultation via video call, voice call, or chat directly through the MediGo platform. The doctor can provide prescriptions and medical certificates digitally.'
    },
    {
      category: 'consultations',
      question: 'Can I choose my preferred doctor?',
      answer: 'Yes! You can browse through our verified doctors, view their profiles, specializations, experience, and patient reviews. You can then book an appointment with your preferred doctor based on their availability.'
    },
    {
      category: 'consultations',
      question: 'What if I need to cancel or reschedule?',
      answer: 'You can cancel or reschedule appointments up to 2 hours before the scheduled time without any charges. Go to "My Appointments" in your dashboard to manage your bookings. Cancellations made less than 2 hours before may incur a fee.'
    },
    {
      category: 'emergency',
      question: 'How fast can an ambulance reach me?',
      answer: 'Our average response time is 10-15 minutes in urban areas. When you request an ambulance, you can track its location in real-time. We dispatch the nearest available ambulance to ensure the quickest response time.'
    },
    {
      category: 'emergency',
      question: 'What information is needed for emergency requests?',
      answer: 'For emergency requests, we need your exact location (GPS or address), contact phone number, type of emergency, and any relevant patient information. Our emergency team will call you immediately to gather additional details while the ambulance is en route.'
    },
    {
      category: 'pharmacy',
      question: 'How does medicine delivery work?',
      answer: 'Upload your prescription through the app or select from your saved prescriptions. Choose from available pharmacies, add medicines to cart, and place your order. Medicines are delivered within 2-4 hours for urgent needs or scheduled for later delivery.'
    },
    {
      category: 'pharmacy',
      question: 'Are the medicines genuine?',
      answer: 'Yes, all pharmacies on MediGo are licensed and verified. We only partner with registered pharmacies that source medicines from authorized distributors. Each medicine delivery includes a bill with batch numbers for verification.'
    },
    {
      category: 'payments',
      question: 'What payment methods are accepted?',
      answer: 'We accept M-Pesa, credit/debit cards, and insurance (for partnered providers). For emergency services, payment can be made after service delivery. Corporate accounts can also set up monthly billing.'
    },
    {
      category: 'payments',
      question: 'Does insurance cover MediGo services?',
      answer: 'Many insurance providers cover MediGo services. During booking, you can check if your insurance is accepted. We are continuously adding more insurance partners. Contact your insurance provider to confirm coverage.'
    },
    {
      category: 'technical',
      question: 'I forgot my password. How do I reset it?',
      answer: 'Click on "Forgot Password" on the login page. Enter your registered email or phone number. You will receive a reset link via email or OTP via SMS to create a new password.'
    },
    {
      category: 'technical',
      question: 'How do I update my medical records?',
      answer: 'Go to your profile dashboard and select "Medical Records". You can upload documents, add allergies, current medications, and medical history. This information is encrypted and only shared with healthcare providers you consult.'
    },
    {
      category: 'safety',
      question: 'How is my medical data protected?',
      answer: 'We use bank-level encryption to protect your data. All medical records are stored securely and only accessible to you and healthcare providers you explicitly grant access to. We comply with all healthcare data protection regulations.'
    },
    {
      category: 'safety',
      question: 'How are doctors verified on MediGo?',
      answer: 'All doctors undergo a thorough verification process including license verification with medical boards, credential checks, and background verification. We continuously monitor and update their status to ensure only qualified professionals are on our platform.'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">How Can We Help You?</h1>
          <p className="text-xl text-primary-50 mb-8">
            Find answers to your questions and learn how to use MediGo effectively
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help topics, FAQs, or guides..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-12 pr-4 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-secondary-800">Frequently Asked Questions</h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            {/* Category Filter */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="font-semibold mb-4 text-secondary-800">Categories</h3>
                <div className="space-y-2">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition ${
                          selectedCategory === category.id
                            ? 'bg-primary-100 text-primary-700'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        <span className="text-sm">{category.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* FAQs List */}
            <div className="md:col-span-3 space-y-4">
              {filteredFaqs.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No FAQs found matching your search.</p>
                  <p className="text-sm text-gray-500 mt-2">Try different keywords or browse categories</p>
                </div>
              ) : (
                filteredFaqs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
                    >
                      <span className="font-medium text-secondary-800">{faq.question}</span>
                      {expandedFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Still Need Help?</h2>
            <p className="mb-8 text-primary-50">
              Our support team is available 24/7 to assist you with any questions or concerns
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <a href="/contact" className="bg-white/20 backdrop-blur rounded-lg p-4 hover:bg-white/30 transition">
                <MessageSquare className="w-8 h-8 mb-2 mx-auto" />
                <h4 className="font-semibold mb-1">Live Chat</h4>
                <p className="text-sm text-primary-100">Chat with support</p>
              </a>
              <a href="tel:+254797310641" className="bg-white/20 backdrop-blur rounded-lg p-4 hover:bg-white/30 transition">
                <Phone className="w-8 h-8 mb-2 mx-auto" />
                <h4 className="font-semibold mb-1">Call Us</h4>
                <p className="text-sm text-primary-100">+254 797 310 641</p>
              </a>
              <a href="mailto:support@medigo.co.ke" className="bg-white/20 backdrop-blur rounded-lg p-4 hover:bg-white/30 transition">
                <Mail className="w-8 h-8 mb-2 mx-auto" />
                <h4 className="font-semibold mb-1">Email</h4>
                <p className="text-sm text-primary-100">support@medigo.co.ke</p>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Help;